import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE, colorPorUmbral } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_BLOQUES_PORHORARIO, MOCK_BLOQUES_ESPERA, MOCK_CAPACIDAD_FRANJA } from './mockDataEjemplos';
import { calcularBloquesHorarios } from './calculosReportes';

// Techo configurable de la escala de ocupación — nunca un 100% fijo. Si algún bloque
// supera este valor, la escala crece con los datos reales (ver scaleMax más abajo).
const OCUPACION_ESCALA_CONFIGURABLE = 300; // %
// Severidad de la barra de ocupación: verde ≤100%, ámbar 100–160%, rojo >160%.
const UMBRAL_OCUPACION_BLOQUE = { bueno: 100, alerta: 160 };

/**
 * Agrega las franjas de 15 min en bloques de 2h — la tabla NO repite el gráfico de
 * demanda: el gráfico muestra la forma, esto el valor puntual agregado. El cálculo vive en
 * calculosReportes.js porque el PDF/Excel exportado imprime exactamente la misma tabla.
 * @param {{porHorario: Array, tiempoEspera: Array, capacidad: number, dias?: number}} props `dias` son los
 *   días del período con turnos: la capacidad es de una franja en UN día, así que la ocupación se calcula
 *   con los turnos promedio por día y no con el acumulado del período.
 */
const TablaBloquesHorarios = ({ porHorario, tiempoEspera, capacidad, dias = 1, compacto = false }) => {
    const bloques = useMemo(
        () => calcularBloquesHorarios(porHorario, tiempoEspera, capacidad, dias),
        [porHorario, tiempoEspera, capacidad, dias]
    );

    // La escala nunca se tapa en 100% fijo: crece con el máximo real del set de datos
    // (con un piso configurable de 300%, pensado para picos de ocupación habituales).
    const escalaMax = useMemo(
        () => Math.max(OCUPACION_ESCALA_CONFIGURABLE, ...bloques.map((b) => b.ocupacion)),
        [bloques]
    );
    const posicion100 = (100 / escalaMax) * 100;

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Resumen por bloque"
                        queMuestra="Agrupa la demanda por franja en bloques de 2 horas para ver de un vistazo en qué franjas horarias del día se pidieron más turnos de los que la capacidad permite atender."
                        ejemploVisual={<TablaBloquesHorarios compacto porHorario={MOCK_BLOQUES_PORHORARIO} tiempoEspera={MOCK_BLOQUES_ESPERA} capacidad={MOCK_CAPACIDAD_FRANJA} />}
                        comoLeerlo={[
                            'Cada fila es un bloque de 2 horas (ej. "08:00–10:00"), sumando las franjas de 15 min que caen adentro.',
                            '"Turnos", "Agendó el bot" (turnos que creó el chatbot), "Atendidos" y "Sobreturnos" son cantidades absolutas del bloque completo, sumando todos los días del período.',
                            '"Espera" es la espera en sala del bloque, ponderada por turnos: sale de los mismos turnos del promedio del encabezado, no del promedio de los promedios de cada franja.',
                            'La "Ocupación" compara los turnos promedio por día del bloque (total del período ÷ días con turnos) contra la capacidad de sus franjas en un día: un mes entero no se mide contra la capacidad de un solo día. La barra es verde si el bloque está ≤100% de su capacidad, ámbar entre 100% y 160%, y roja por encima de 160%.',
                            'La marca vertical tenue sobre la barra es el punto exacto del 100% de capacidad — la escala de la barra se ajusta al máximo real de los datos, nunca se corta ahí.',
                        ]}
                        analisis="El bloque 08:00–10:00 llega a 200% de ocupación (10 turnos contra una capacidad de 5 en ese tramo) y concentra casi todos los sobreturnos — es el que hay que reforzar primero. El bloque 10:00–12:00 está justo al límite (100%, cada franja con su turno normal, sin sobrecarga): no tiene margen libre, pero tampoco está en rojo — si se puede correr algún turno del bloque pico hacia acá, todavía entra sin generar un nuevo sobreturno."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Resumen por bloque</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Franjas de 15 min agrupadas en bloques de 2 horas.
                    </Typography>
                </>
            )}
            <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FUENTE }}>
                    <Box component="thead">
                        <Box component="tr">
                            {['Bloque', 'Turnos', 'Agendó el bot', 'Atendidos', 'Sobreturnos', 'Espera', 'Ocupación'].map((h) => (
                                <Box component="th" key={h} sx={{
                                    textAlign: h === 'Bloque' ? 'left' : 'right', padding: '8px 10px',
                                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                                    color: COLOR.grisTexto, borderBottom: '1px solid #e2e8f0',
                                }}>
                                    {h}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {bloques.map((b) => (
                            <Box component="tr" key={b.bloque}>
                                <Celda align="left">{b.bloque}</Celda>
                                <Celda>{b.turnos}</Celda>
                                <Celda>{b.bot}</Celda>
                                <Celda>{b.atendidos}</Celda>
                                <Celda>{b.sobreturnos}</Celda>
                                <Celda>{b.espera != null ? `${b.espera.toFixed(0)} min` : '—'}</Celda>
                                <Box component="td" sx={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                                        <Box sx={{ position: 'relative', width: 60, height: 6, borderRadius: 3, background: COLOR.gris, overflow: 'hidden' }}>
                                            <Box sx={{
                                                width: `${(b.ocupacion / escalaMax) * 100}%`, height: '100%',
                                                background: colorPorUmbral(b.ocupacion, UMBRAL_OCUPACION_BLOQUE, true),
                                            }} />
                                            <Box sx={{
                                                position: 'absolute', top: 0, bottom: 0, left: `${posicion100}%`,
                                                width: '1px', background: COLOR.negro, opacity: 0.35,
                                            }} />
                                        </Box>
                                        <Typography sx={{ fontFamily: FUENTE, fontSize: 12, fontVariantNumeric: 'tabular-nums', color: COLOR.negro, width: 40 }}>
                                            {b.ocupacion.toFixed(0)}%
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
            {!compacto && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginTop: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box sx={{ width: 1, height: 12, background: COLOR.negro, opacity: 0.35 }} />
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>100% capacidad</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: COLOR.verde }} />
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>≤100%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: COLOR.ambar }} />
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>100–160%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: COLOR.rojo }} />
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>&gt;160%</Typography>
                    </Box>
                </Box>
            )}
        </Card>
    );
};

const Celda = ({ children, align = 'right' }) => (
    <Box component="td" sx={{
        padding: '8px 10px', textAlign: align, borderBottom: '1px solid #F1F5F9',
        fontSize: 13, color: COLOR.negro, fontVariantNumeric: 'tabular-nums',
    }}>
        {children}
    </Box>
);

export default TablaBloquesHorarios;
